// Core
// Components
import * as RadixCheckbox from '@radix-ui/react-checkbox'
import React, {
  type ComponentProps,
  createContext,
  useContext,
  useId,
} from 'react'
// Libs
import { twMerge } from 'tailwind-merge'

export function Root({
  className,
  children,
  ...props
}: RadixCheckbox.CheckboxProps & { children?: React.ReactNode }): JSX.Element {
  const customId = useId()

  return (
    <CheckboxContext.Provider value={{ customId, ...props }}>
      <div
        className={twMerge('flex items-center justify-center gap-2', className)}
      >
        {children}
      </div>
    </CheckboxContext.Provider>
  )
}

export function Trigger({ className }: { className?: string }): JSX.Element {
  const { customId, ...props } = useContext(CheckboxContext)
  delete props.className

  return (
    <RadixCheckbox.Root
      id={customId}
      className={twMerge(
        'w-[18px] h-[18px] rounded-[4px] data-[state=checked]:bg-primary disabled:bg-text-disabled data-[state=checked]:border-none border border-text-primary bg-transparent flex items-center justify-center text-white',
        className,
      )}
      {...props}
    >
      <RadixCheckbox.Indicator className="text-white">
        <svg
          width="14"
          height="10"
          viewBox="0 0 14 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.62445 5.50452L4.57683 8.4734C4.65593 8.55524 4.75166 8.61753 4.85706 8.65574C4.96247 8.69394 5.07489 8.70711 5.18617 8.69427C5.29837 8.68293 5.40709 8.64653 5.50494 8.58756C5.6028 8.52859 5.68749 8.44843 5.75325 8.35253L12.6492 1.08787"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  )
}

export function Label({
  className,
  ...props
}: ComponentProps<'label'>): JSX.Element {
  const { customId, disabled } = useContext(CheckboxContext)
  return (
    <label
      data-disabled={disabled}
      htmlFor={customId}
      className={twMerge(
        'text-xs text-text-secondary data-[disabled=true]:text-text-disabled data-[disabled=false]:cursor-pointer',
        className,
      )}
      {...props}
    />
  )
}

export function Default({
  label,
  labelSide = 'right',
  labelStyle,
  ...props
}: ComponentProps<typeof Root> & {
  label: string
  labelSide?: 'left' | 'right'
  labelStyle?: string
}): JSX.Element {
  return (
    <Root {...props}>
      {labelSide && labelSide === 'left' && (
        <Label className={labelStyle}>{label}</Label>
      )}
      <Trigger />
      {labelSide && labelSide === 'right' && (
        <Label className={labelStyle}>{label}</Label>
      )}
    </Root>
  )
}

interface CheckboxProps extends RadixCheckbox.CheckboxProps {
  customId: string
}

export const CheckboxContext = createContext<CheckboxProps>({} as CheckboxProps)
